import React, { useState, useEffect, useMemo, useRef, useCallback } from 'react'
import { useNavigate, useParams, useLocation, Link } from 'react-router-dom'
import { Form, Button, Container, Row, Col, Alert, Spinner, Table, Badge, Card, OverlayTrigger, Tooltip } from 'react-bootstrap'
import Select from 'react-select'
import AsyncSelect from 'react-select/async'
import { recipeAPI } from '../api/recipeAPI'
import { stockItemAPI } from '../api/stockItemAPI'
import { itemFamilyAPI } from '../api/itemFamilyAPI'
import { formatErrorResponse } from '../utils/errorHandlers'
import { highlightText } from '../utils/commonUtils'
import { usePermissions } from '../contexts/PermissionContext'
import ConfirmationModal from '../components/ConfirmationModal'
import StockItemModal from '../components/purchase_order/modals/StockItemModal'
import StockItemInfo from '../components/StockItemInfo'
import CopyableText from '../components/CopyableText'
import QuantityPicker, { isSuperUnit, getPieceLabel } from '../components/QuantityPicker'
import QuantityDisplay from '../components/QuantityDisplay'

// UOM options matching backend UNITS_OF_MEASURE_CHOICES
const UOM_OPTIONS = [
    { value: 'bag', label: 'Bag' },
    { value: 'bulk', label: 'Bulk' },
    { value: 'clear_film', label: 'Clear Film' },
    { value: 'ct', label: 'Count' },
    { value: 'film', label: 'Film' },
    { value: 'jar', label: 'Jar' },
    { value: 'pallet', label: 'Pallet' },
    { value: 'tote_bag', label: 'Tote Bag' },
]

// Weight unit options matching backend ITEM_SIZE_UNITS_CHOICES
const WEIGHT_UNIT_OPTIONS = [
    { value: 'lbs', label: 'Pounds (lbs)' },
    { value: 'oz', label: 'Ounces (oz)' },
]

// Input mode options
const INPUT_MODE_OPTIONS = [
    { value: 'cases', label: 'Quantity' },
    { value: 'weight', label: 'Weight' },
]

// UOMs that don't support weight input mode
const NO_WEIGHT_UOMS = ['ct', 'pallet', 'clear_film']

function RecipeUpdateScreen() {
    const { hasPermission, loading: permissionsLoading } = usePermissions()
    const { id } = useParams()
    const navigate = useNavigate()
    const location = useLocation()
    const backTo = location.state?.backTo || '/recipes'
    const backLabel = location.state?.backLabel || 'Back to List'
    const [returnFilters] = useState(location.state?.returnFilters || '')
    const mode = id ? 'update' : 'create'

    const [error, setError] = useState(null)
    const [loading, setLoading] = useState(mode === 'update')
    const [recipe, setRecipe] = useState(null)

    // Recipe form fields
    const [name, setName] = useState('')
    const [itemNumber, setItemNumber] = useState('')
    const [description, setDescription] = useState('')
    const [outputStockItem, setOutputStockItem] = useState(null)
    const [yieldQuantity, setYieldQuantity] = useState('1')
    const [isActive, setIsActive] = useState(true)

    // Data for dropdowns
    const [manufacturedStockItems, setManufacturedStockItems] = useState([])
    const [allItemFamilies, setAllItemFamilies] = useState([])

    // Debounce timer for async ingredient search
    const searchTimerRef = useRef(null)

    // Ingredient form state
    const [selectedStockItem, setSelectedStockItem] = useState(null)
    const [selectedItemFamily, setSelectedItemFamily] = useState(null)
    const [ingredientQuantity, setIngredientQuantity] = useState('')
    const [ingredientNotes, setIngredientNotes] = useState('')
    const [addingIngredient, setAddingIngredient] = useState(false)
    // Input mode state
    const [inputMode, setInputMode] = useState('cases')
    const [weightValue, setWeightValue] = useState('')
    const [weightUnit, setWeightUnit] = useState(WEIGHT_UNIT_OPTIONS[0]) // Default to lbs

    // Inline editing state
    const [editingIngredientId, setEditingIngredientId] = useState(null)
    const [editQuantity, setEditQuantity] = useState('')
    const [savingEdit, setSavingEdit] = useState(false)
    const originalEditQuantityRef = useRef(null)

    // Delete modal
    const [showDeleteModal, setShowDeleteModal] = useState(false)

    // Stock item creation modal
    const [showStockItemModal, setShowStockItemModal] = useState(false)

    // Pending ingredients for create mode (stored locally until recipe is created)
    const [pendingIngredients, setPendingIngredients] = useState([])

    useEffect(() => {
        fetchManufacturedStockItems()
        fetchAllItemFamilies()
        if (mode === 'update') {
            fetchRecipe()
        }
    }, [id, mode])

    const fetchRecipe = async () => {
        try {
            setLoading(true)
            const data = await recipeAPI.getById(id)
            setRecipe(data)
            setName(data.name)
            setItemNumber(data.item_number || '')
            setDescription(data.description || '')
            setYieldQuantity(data.yield_quantity?.toString() || '1')
            setIsActive(data.is_active)
            // Use output_stock_item_data from the API response directly
            if (data.output_stock_item_data) {
                setOutputStockItem(data.output_stock_item_data)
            }
        } catch (error) {
            console.error('Error fetching recipe:', error)
            setError('Failed to load recipe data')
        } finally {
            setLoading(false)
        }
    }

    const fetchManufacturedStockItems = async () => {
        try {
            const response = await stockItemAPI.getAll(1, 500, '', undefined, undefined, undefined, 'true')
            setManufacturedStockItems(response.results || [])
        } catch (error) {
            console.error('Error fetching manufactured stock items:', error)
        }
    }

    const fetchAllItemFamilies = async () => {
        try {
            const response = await itemFamilyAPI.getAll(1, 1000, '')
            setAllItemFamilies(response.results || [])
        } catch (error) {
            console.error('Error fetching item families:', error)
        }
    }

    // Async search for ingredient stock items
    const loadIngredientOptions = useCallback((inputValue, callback) => {
        if (searchTimerRef.current) {
            clearTimeout(searchTimerRef.current)
        }
        if (!inputValue || inputValue.length < 2) {
            callback([])
            return
        }
        searchTimerRef.current = setTimeout(async () => {
            try {
                const response = await stockItemAPI.getAll(1, 50, inputValue)
                const results = (response.results || []).filter(item => {
                    if (recipe?.ingredients?.some(ing => ing.stock_item === item._id)) return false
                    if (pendingIngredients.some(ing => ing.stock_item === item._id)) return false
                    return true
                })
                callback(results)
            } catch (error) {
                console.error('Error searching stock items:', error)
                callback([])
            }
        }, 300)
    }, [recipe?.ingredients, pendingIngredients])

    // Filter out item families already used as ingredients
    const availableItemFamilies = allItemFamilies.filter(family => {
        // Check existing ingredients (update mode)
        if (recipe?.ingredients?.some(ing => ing.item_family === family._id)) return false
        // Check pending ingredients (create mode)
        if (pendingIngredients.some(ing => ing.item_family === family._id)) return false
        return true
    })

    // Output stock item options with "Create New" option
    const outputStockItemOptions = useMemo(() => {
        const createNewOption = {
            _id: 'create_new',
            description: '+ Create New Stock Item',
            isCreateNew: true
        }
        return [createNewOption, ...manufacturedStockItems]
    }, [manufacturedStockItems])

    // Handle output stock item selection
    const handleOutputStockItemChange = (selected) => {
        if (selected?.isCreateNew) {
            setShowStockItemModal(true)
            return
        }
        setOutputStockItem(selected)
        if (selected) {
            setName(itemNumber
                ? `${selected.description} (${itemNumber})`
                : selected.description)
        }
    }

    // Handle stock item creation from modal
    const handleStockItemCreated = async (newStockItem) => {
        // Refresh manufactured stock items list
        await fetchManufacturedStockItems()
        // Set the newly created stock item as output
        setOutputStockItem(newStockItem)
        setName(itemNumber
            ? `${newStockItem.description} (${itemNumber})`
            : newStockItem.description)
        setShowStockItemModal(false)
    }

    // Handle stock item selection
    const handleStockItemSelect = (stockItem) => {
        setSelectedStockItem(stockItem)
        setSelectedItemFamily(null) // Clear other selection
        if (stockItem) {
            // Set default weight unit based on stock item's item_size_units
            if (stockItem.item_size_units) {
                const weightUnitOption = WEIGHT_UNIT_OPTIONS.find(opt => opt.value === stockItem.item_size_units)
                if (weightUnitOption) {
                    setWeightUnit(weightUnitOption)
                }
            }
            // Reset input mode if current mode is not available for this stock item
            if (inputMode === 'weight' && NO_WEIGHT_UOMS.includes(stockItem.uom)) {
                setInputMode('cases')
            }
        }
    }

    const handleItemFamilySelect = (itemFamily) => {
        setSelectedItemFamily(itemFamily)
        setSelectedStockItem(null) // Clear other selection
        if (itemFamily) {
            // Set default weight unit based on item family's item_size_units (from first stock item)
            if (itemFamily.item_size_units) {
                const weightUnitOption = WEIGHT_UNIT_OPTIONS.find(opt => opt.value === itemFamily.item_size_units)
                if (weightUnitOption) {
                    setWeightUnit(weightUnitOption)
                }
            }
            // Reset input mode if current mode is not available for this item family
            if (inputMode === 'weight' && NO_WEIGHT_UOMS.includes(itemFamily.uom)) {
                setInputMode('cases')
            }
        }
    }

    const resetIngredientForm = () => {
        setSelectedStockItem(null)
        setSelectedItemFamily(null)
        setIngredientQuantity('')
        setIngredientNotes('')
        setInputMode('cases')
        setWeightValue('')
        setWeightUnit(WEIGHT_UNIT_OPTIONS[0])
    }

    const submitHandler = async () => {
        if (!outputStockItem) {
            setError('Output Product is required')
            return
        }

        if (!name.trim()) {
            setError('Recipe Name is required')
            return
        }

        const recipeData = {
            name: name.trim(),
            item_number: itemNumber || '',
            description: description || null,
            output_stock_item: outputStockItem._id,
            yield_quantity: 1,
            is_active: isActive
        }

        try {
            if (mode === 'create') {
                // Create the recipe first
                const newRecipe = await recipeAPI.create(recipeData)

                // Add all pending ingredients
                for (const ingredient of pendingIngredients) {
                    const ingredientData = {
                        stock_item: ingredient.stock_item || null,
                        item_family: ingredient.item_family || null,
                        waste_percentage: ingredient.waste_percentage,
                        notes: ingredient.notes || null,
                        input_mode: ingredient.input_mode,
                    }
                    // Add mode-specific fields
                    if (ingredient.input_mode === 'cases') {
                        ingredientData.quantity = ingredient.quantity
                    } else if (ingredient.input_mode === 'weight') {
                        ingredientData.weight_value = ingredient.weight_value
                        ingredientData.weight_unit = ingredient.weight_unit
                    }
                    await recipeAPI.addIngredient(newRecipe._id, ingredientData)
                }

                navigate(backTo + returnFilters)
            } else {
                await recipeAPI.update(id, recipeData)
                navigate(backTo + returnFilters)
            }
        } catch (error) {
            setError(formatErrorResponse(error))
        }
    }

    const handleAddIngredient = async () => {
        if (!selectedStockItem && !selectedItemFamily) {
            setError('Please select a Stock Item or Item Family')
            return
        }

        // Validate based on input mode
        if (inputMode === 'cases') {
            if (!ingredientQuantity || parseFloat(ingredientQuantity) <= 0) {
                setError('Quantity is required and must be greater than 0')
                return
            }
        } else if (inputMode === 'weight') {
            if (!weightValue || parseFloat(weightValue) <= 0) {
                setError('Weight is required and must be greater than 0')
                return
            }
        }

        let quantity = 0

        // Get properties from selected stock item or item family
        const selectedItem = selectedStockItem || selectedItemFamily

        // Calculate quantity based on input mode
        if (inputMode === 'cases') {
            quantity = parseFloat(ingredientQuantity)
        } else if (inputMode === 'weight') {
            // Estimate quantity for display (backend will recalculate)
            if (selectedItem?.item_size && selectedItem?.case_pack) {
                const weightVal = parseFloat(weightValue)
                let weightInItemUnits = weightVal
                // Convert to item's units if different
                if (weightUnit.value !== selectedItem.item_size_units) {
                    if (weightUnit.value === 'oz' && selectedItem.item_size_units === 'lbs') {
                        weightInItemUnits = weightVal / 16
                    } else if (weightUnit.value === 'lbs' && selectedItem.item_size_units === 'oz') {
                        weightInItemUnits = weightVal * 16
                    }
                }
                const items = weightInItemUnits / parseFloat(selectedItem.item_size)
                quantity = items / selectedItem.case_pack
            }
        }

        // Build ingredient data for API (unit_of_measure is derived from stock_item/item_family)
        const ingredientData = {
            stock_item: selectedStockItem?._id || null,
            item_family: selectedItemFamily?._id || null,
            notes: ingredientNotes || null,
            input_mode: inputMode,
        }

        // Add mode-specific fields
        if (inputMode === 'cases') {
            ingredientData.quantity = parseFloat(ingredientQuantity)
        } else if (inputMode === 'weight') {
            ingredientData.weight_value = parseFloat(weightValue)
            ingredientData.weight_unit = weightUnit.value
        }

        // For display purposes in create mode
        const displayData = {
            ...ingredientData,
            ingredient_name: selectedStockItem?.description || selectedItemFamily?.name,
            quantity: quantity,

            unit_of_measure: selectedItem?.uom,
            // Item families use null case_pack (quantities in sub-units, no single case_pack)
            case_pack: selectedItemFamily ? null : (selectedItem?.case_pack || 1),
            sub_unit_quantity: null,
            gtin: selectedStockItem?.gtin || null,
            stock_item_data: selectedStockItem || null,
            stock_item_image: selectedStockItem?.image || selectedItemFamily?.image || null,
            _tempId: Date.now() // Temporary ID for local state management
        }

        if (mode === 'create') {
            // Add to local pending list
            setPendingIngredients([...pendingIngredients, displayData])
            resetIngredientForm()
            setError(null)
        } else {
            // Update mode - call API
            try {
                setAddingIngredient(true)
                const updatedRecipe = await recipeAPI.addIngredient(id, ingredientData)
                setRecipe(updatedRecipe)
                resetIngredientForm()
                setError(null)
            } catch (error) {
                console.error('Error adding ingredient:', error)
                setError(`Failed to add ingredient: ${error.response?.data?.error || error.message}`)
            } finally {
                setAddingIngredient(false)
            }
        }
    }

    const handleRemoveIngredient = async (ingredientId) => {
        setEditingIngredientId(null)
        try {
            const updatedRecipe = await recipeAPI.deleteIngredient(id, ingredientId)
            setRecipe(updatedRecipe)
        } catch (error) {
            console.error('Error removing ingredient:', error)
            alert(`Failed to remove ingredient: ${error.response?.data?.error || error.message}`)
        }
    }

    const handleEditIngredient = (ingredient) => {
        setEditingIngredientId(ingredient._id)
        setEditQuantity(parseFloat(ingredient.quantity))
        originalEditQuantityRef.current = ingredient.quantity
    }

    const handleCancelEdit = () => {
        setEditingIngredientId(null)
        setEditQuantity('')
    }

    const handleSaveEdit = async (ingredientId) => {
        const qty = parseFloat(editQuantity)
        if (!qty || qty <= 0) {
            setError('Quantity must be greater than 0')
            return
        }
        setSavingEdit(true)
        try {
            const originalQty = parseFloat(originalEditQuantityRef.current)
            const quantityChanged = qty !== originalQty
            if (!quantityChanged) {
                setEditingIngredientId(null)
                setSavingEdit(false)
                return
            }
            const updateData = { quantity: qty, input_mode: 'cases' }
            const updatedRecipe = await recipeAPI.updateIngredient(id, ingredientId, updateData)
            setRecipe(updatedRecipe)
            setEditingIngredientId(null)
        } catch (error) {
            console.error('Error updating ingredient:', error)
            setError(`Failed to update ingredient: ${error.response?.data?.error || error.message}`)
        } finally {
            setSavingEdit(false)
        }
    }

    const handleRemovePendingIngredient = (tempId) => {
        setPendingIngredients(pendingIngredients.filter(ing => ing._tempId !== tempId))
    }

    const handleDeleteClick = () => {
        setShowDeleteModal(true)
    }

    const confirmDelete = async () => {
        try {
            await recipeAPI.delete(id)
            navigate(backTo + returnFilters)
        } catch (error) {
            console.error('Error deleting recipe:', error)
            alert(`Failed to delete recipe: ${error.response?.data?.message || error.message}`)
        } finally {
            setShowDeleteModal(false)
        }
    }

    const handlePrintRecipe = () => {
        const unitLabels = { film: 'Rolls', pallet: 'Pallets', clear_film: 'Rolls' }
        const pieceLabels = { film: 'Impressions', clear_film: 'Grams' }
        const getUnit = (uom) => unitLabels[uom] || 'Cases'
        const getPiece = (uom) => pieceLabels[uom] || 'Items'
        const isSuper = (uom) => uom === 'pallet' || uom === 'film' || uom === 'clear_film'
        const fmt = (v) => parseFloat(parseFloat(v).toFixed(4)).toString()

        const ingredientRows = recipe.ingredients.map((ing, index) => {
            let qtyText = ''
            if (ing.input_mode === 'weight' && ing.weight_value) {
                qtyText = `${ing.weight_value} ${ing.weight_unit}`
            } else {
                const num = parseFloat(ing.quantity)
                const cp = ing.case_pack || 1
                const uom = ing.unit_of_measure || ''
                if (!cp || cp <= 1) {
                    qtyText = isSuper(uom) ? `${fmt(num)} ${getPiece(uom)}` : fmt(num)
                } else {
                    const exactPieces = isSuper(uom) ? num : num * cp
                    const totalPieces = Math.round(exactPieces)
                    const units = Math.floor(totalPieces / cp)
                    const pieces = totalPieces % cp
                    if (units > 0 && pieces === 0) {
                        qtyText = `${units} ${getUnit(uom)}`
                    } else {
                        const parts = []
                        if (units > 0) parts.push(`${units} ${getUnit(uom)}`)
                        if (pieces > 0) parts.push(`${pieces} ${getPiece(uom)}`)
                        parts.push(`total ${fmt(totalPieces / cp)} ${getUnit(uom)}`)
                        qtyText = parts.join(', ')
                    }
                }
                if (ing.item_family && !isSuper(ing.unit_of_measure || '')) {
                    qtyText += ' items'
                }
            }
            const type = ing.stock_item ? 'Stock Item' : 'Item Family'
            const ingredientName = ing.ingredient_name || ing.stock_item_name || ing.item_family_name || ''
            const gtin = ing.item_family ? '' : (ing.gtin || '')
            return `<tr>
                <td style="text-align:center">${index + 1}</td>
                <td>${ingredientName}</td>
                <td>${gtin}</td>
                <td>${type}</td>
                <td>${qtyText}</td>
                <td style="text-align:center">${ing.waste_percentage}%</td>
            </tr>`
        }).join('')

        const out = recipe.output_stock_item_data
        const printDate = new Date().toLocaleDateString('en-US')

        const html = `<html><head><title>Recipe: ${recipe.name}</title>
        <style>
            body { font-family: Arial, sans-serif; margin: 40px; color: #000; background: #fff; }
            h1 { margin-bottom: 5px; font-size: 24px; }
            h2 { font-size: 18px; margin-top: 24px; margin-bottom: 8px; }
            .details { margin-bottom: 10px; }
            .details p { margin: 3px 0; font-size: 14px; }
            table { width: 100%; border-collapse: collapse; margin-top: 8px; font-size: 14px; }
            th, td { border: 1px solid #333; padding: 6px 10px; text-align: left; }
            th { background-color: #f0f0f0; font-weight: bold; }
            .footer { margin-top: 30px; font-size: 11px; color: #888; }
            @media print { body { margin: 20px; } }
        </style></head><body>
        <h1>${recipe.name}</h1>
        <div class="details">
            <p><strong>Output Product:</strong> ${out?.description || ''}</p>
            ${out?.gtin ? `<p><strong>GTIN:</strong> ${out.gtin}</p>` : ''}
            ${out?.brand_name ? `<p><strong>Brand:</strong> ${out.brand_name}</p>` : ''}
            ${recipe.item_number ? `<p><strong>Item Number:</strong> ${recipe.item_number}</p>` : ''}
            ${recipe.description ? `<p><strong>Description:</strong> ${recipe.description}</p>` : ''}
        </div>
        <h2>Ingredients</h2>
        <table>
            <thead><tr>
                <th style="width:30px;text-align:center">#</th>
                <th>Ingredient</th>
                <th>GTIN</th>
                <th>Type</th>
                <th>Quantity</th>
                <th style="text-align:center">Waste %</th>
            </tr></thead>
            <tbody>${ingredientRows}</tbody>
        </table>
        <div class="footer">Printed on: ${printDate}</div>
        </body></html>`

        const printWindow = window.open('', '_blank')
        printWindow.document.write(html)
        printWindow.document.close()
        setTimeout(() => { printWindow.print() }, 100)
    }

    if (loading || permissionsLoading) {
        return (
            <Container className="py-4">
                <div className="text-center">
                    <Spinner animation="border" role="status">
                        <span className="visually-hidden">Loading...</span>
                    </Spinner>
                </div>
            </Container>
        )
    }

    // For create mode, require recipes.modify permission
    // For update mode, allow viewing with recipes.view, but disable editing without recipes.modify
    if (mode === 'create' && !hasPermission('recipes.modify')) {
        return (
            <Container className="py-3">
                <Alert variant="danger">
                    You don&apos;t have permission to create recipes.
                </Alert>
            </Container>
        )
    }

    const canModify = hasPermission('recipes.modify')

    if (mode === 'update' && !recipe) {
        return (
            <Container className="py-4">
                <Alert variant="warning">Recipe not found</Alert>
                <Button variant="outline-primary" onClick={() => navigate(backTo + returnFilters)}>
                    {backLabel}
                </Button>
            </Container>
        )
    }

    return (
        <Container>
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <h1>{mode === 'create' ? 'Create Recipe' : (canModify ? 'Update Recipe' : 'Recipe Details')}</h1>

                    <Form.Group className="mb-4" controlId="outputStockItem">
                        <Form.Label>Output Product*</Form.Label>
                        <div className="d-flex align-items-center">
                            {outputStockItem?.image && (
                                <img
                                    src={outputStockItem.image}
                                    alt={outputStockItem.description}
                                    style={{ width: '60px', height: '60px', objectFit: 'contain' }}
                                    className="me-3"
                                />
                            )}
                            <div className="flex-grow-1">
                                <Select
                                    value={outputStockItem}
                                    onChange={handleOutputStockItemChange}
                                    options={outputStockItemOptions}
                                    getOptionLabel={option => option.description}
                                    getOptionValue={option => option._id}
                                    filterOption={(option, inputValue) => {
                                        if (option.data.isCreateNew) return true
                                        if (!inputValue) return true
                                        const searchTerm = inputValue.toLowerCase()
                                        const description = option.data.description?.toLowerCase() || ''
                                        return description.includes(searchTerm)
                                    }}
                                    placeholder="Select output product..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={!canModify}
                                />
                            </div>
                            {outputStockItem?._id && outputStockItem._id !== 'create_new' && (
                                <Link to={`/stock-item/${outputStockItem._id}`} target="_blank" className="ms-2">
                                    <i className="fas fa-external-link-alt"></i>
                                </Link>
                            )}
                        </div>
                    </Form.Group>
                    {outputStockItem?.brand_name && (
                        <p className="mt-2 mb-3">Brand: {outputStockItem.brand_name}</p>
                    )}

                    <Form.Group className="mb-4" controlId="recipeName">
                        <Form.Label>Recipe Name*</Form.Label>
                        <Form.Control
                            type="text"
                            value={name}
                            onChange={(e) => setName(e.target.value)}
                            placeholder="Recipe name"
                            maxLength={255}
                            disabled={!canModify}
                            required
                        />
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="itemNumber">
                        <Form.Label>Item Number</Form.Label>
                        <Form.Control
                            type="text"
                            value={itemNumber}
                            onChange={(e) => {
                                const newItemNumber = e.target.value
                                setItemNumber(newItemNumber)
                                if (outputStockItem && outputStockItem._id !== 'create_new') {
                                    setName(newItemNumber
                                        ? `${outputStockItem.description} (${newItemNumber})`
                                        : outputStockItem.description)
                                }
                            }}
                            placeholder="Optional item number"
                            maxLength={50}
                            disabled={!canModify}
                        />
                    </Form.Group>

                    <Form.Group className="mb-4" controlId="description">
                        <Form.Label>Description</Form.Label>
                        <Form.Control
                            as="textarea"
                            rows={2}
                            value={description}
                            onChange={(e) => setDescription(e.target.value)}
                            placeholder="Optional description"
                            disabled={!canModify}
                        />
                    </Form.Group>

                </Col>
            </Row>

            {/* Ingredients Section - show in both create and update modes */}
            <div className="d-flex align-items-center mb-2">
                <h4 className="mb-0">Ingredients</h4>
            </div>

            {recipe?.in_production?.is_active && (
                <Alert variant="warning">
                    This recipe is used in active production LOT(s): <strong>{recipe.in_production.lot_numbers.join(', ')}</strong>. Ingredients cannot be added, edited, or removed.
                </Alert>
            )}

            {/* Add Ingredient Card - only show if user can modify */}
            {canModify && !recipe?.in_production?.is_active && (
            <Row className="justify-content-left">
                <Col md={8} lg={6}>
                    <div className="card mb-3">
                        <div className="card-header bg-body-secondary">
                            <h5 className="mb-0">Add Ingredient</h5>
                        </div>
                        <div className="card-body">
                            <Form.Group className="mb-3" controlId="stockItemSelect">
                                <Form.Label>Stock Item</Form.Label>
                                <AsyncSelect
                                    value={selectedStockItem}
                                    onChange={handleStockItemSelect}
                                    loadOptions={loadIngredientOptions}
                                    getOptionLabel={option => `${option.description}${option.gtin ? ` (${option.gtin})` : ''}`}
                                    getOptionValue={option => option._id}
                                    formatOptionLabel={(option, { inputValue, context }) => {
                                        if (context === 'value') {
                                            return `${option.description}${option.gtin ? ` (${option.gtin})` : ''}`
                                        }
                                        return (
                                            <span>
                                                {highlightText(option.description, inputValue)}
                                                {option.gtin ? <> ({highlightText(option.gtin, inputValue)})</> : ''}
                                            </span>
                                        )
                                    }}
                                    noOptionsMessage={({ inputValue }) =>
                                        !inputValue || inputValue.length < 2
                                            ? 'Type at least 2 characters to search...'
                                            : 'No matching items found'
                                    }
                                    placeholder="Search by name or GTIN..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable={true}
                                    isDisabled={selectedItemFamily !== null}
                                    defaultOptions={false}
                                />
                            </Form.Group>

                            <Form.Group className="mb-3" controlId="itemFamilySelect">
                                <Form.Label>Item Family</Form.Label>
                                <Select
                                    value={selectedItemFamily}
                                    onChange={handleItemFamilySelect}
                                    options={availableItemFamilies}
                                    getOptionLabel={option => option.name}
                                    getOptionValue={option => option._id}
                                    filterOption={(option, inputValue) => {
                                        if (!inputValue) return true
                                        return option.data.name.toLowerCase().includes(inputValue.toLowerCase())
                                    }}
                                    placeholder="Or select item family..."
                                    className="react-select-container"
                                    classNamePrefix="react-select"
                                    isClearable={true}
                                    isSearchable={true}
                                    isDisabled={selectedStockItem !== null}
                                />
                                <Form.Text className="text-muted">
                                    Use item family for flexible ingredient substitution
                                </Form.Text>
                            </Form.Group>

                            {/* Input Mode Selector - only show when weight mode is available */}
                            {(selectedStockItem || selectedItemFamily) && (() => {
                                const selectedItem = selectedStockItem || selectedItemFamily
                                const weightAvailable = !NO_WEIGHT_UOMS.includes(selectedItem?.uom)
                                if (!weightAvailable) return null
                                return (
                                    <Form.Group className="mb-3" controlId="inputMode">
                                        <Form.Label>Input Mode</Form.Label>
                                        <div>
                                            {INPUT_MODE_OPTIONS.map((option) => (
                                                <Form.Check
                                                    key={option.value}
                                                    inline
                                                    type="radio"
                                                    id={`inputMode-${option.value}`}
                                                    label={option.label}
                                                    name="inputMode"
                                                    value={option.value}
                                                    checked={inputMode === option.value}
                                                    onChange={(e) => setInputMode(e.target.value)}
                                                />
                                            ))}
                                        </div>
                                    </Form.Group>
                                )
                            })()}

                            <Row className="mb-3">
                                {/* Quantity input - shown for cases mode */}
                                {inputMode === 'cases' && (() => {
                                    const selectedItem = selectedStockItem || selectedItemFamily
                                    // Item families use casePack=1 (quantities stored in sub-units)
                                    const casePack = selectedItemFamily ? 1 : (selectedItem?.case_pack || 1)
                                    const isSuperUom = selectedItemFamily && isSuperUnit(selectedItem?.uom || '')
                                    return (
                                        <Col>
                                            <Form.Group controlId="ingredientQuantity">
                                                <Form.Label>Quantity{isSuperUom ? ` (${getPieceLabel(selectedItem?.uom || '')})` : ''}*</Form.Label>
                                                <QuantityPicker
                                                    casePack={casePack}
                                                    uom={selectedItem?.uom || ''}
                                                    value={ingredientQuantity}
                                                    onChange={(qty) => setIngredientQuantity(qty)}
                                                />
                                            </Form.Group>
                                        </Col>
                                    )
                                })()}

                                {/* Weight inputs - shown for weight mode */}
                                {inputMode === 'weight' && (
                                    <>
                                        <Col>
                                            <Form.Group controlId="weightValue">
                                                <Form.Label>Weight*</Form.Label>
                                                <Form.Control
                                                    type="number"
                                                    step="0.001"
                                                    min="0"
                                                    value={weightValue}
                                                    onChange={(e) => setWeightValue(e.target.value)}
                                                    placeholder="0.00"
                                                />
                                            </Form.Group>
                                        </Col>
                                        <Col xs={3}>
                                            <Form.Group controlId="weightUnit">
                                                <Form.Label>Unit</Form.Label>
                                                <Select
                                                    value={weightUnit}
                                                    onChange={setWeightUnit}
                                                    options={WEIGHT_UNIT_OPTIONS}
                                                    className="react-select-container"
                                                    classNamePrefix="react-select"
                                                    isClearable={false}
                                                />
                                            </Form.Group>
                                        </Col>
                                    </>
                                )}

                                {/* UOM display - derived from stock item or item family */}
                                {(selectedStockItem || selectedItemFamily) && (() => {
                                    const selectedItem = selectedStockItem || selectedItemFamily
                                    return (
                                        <Col xs={2}>
                                            <Form.Group controlId="ingredientUom">
                                                <Form.Label>UOM</Form.Label>
                                                <Form.Control
                                                    type="text"
                                                    value={UOM_OPTIONS.find(opt => opt.value === selectedItem.uom)?.label || selectedItem.uom}
                                                    disabled
                                                    readOnly
                                                />
                                            </Form.Group>
                                        </Col>
                                    )
                                })()}
                                            </Row>

                            {/* Notes field - commented out for future use
                            <Form.Group className="mb-3" controlId="ingredientNotes">
                                <Form.Label>Notes</Form.Label>
                                <Form.Control
                                    type="text"
                                    value={ingredientNotes}
                                    onChange={(e) => setIngredientNotes(e.target.value)}
                                    placeholder="Optional notes"
                                />
                            </Form.Group>
                            */}

                            <div className="text-end">
                                <Button
                                    variant="success"
                                    onClick={handleAddIngredient}
                                    disabled={
                                        (!selectedStockItem && !selectedItemFamily) ||
                                        addingIngredient ||
                                        (inputMode === 'cases' && !ingredientQuantity) ||
                                        (inputMode === 'weight' && !weightValue)
                                    }
                                >
                                    {addingIngredient ? (
                                        <>
                                            <Spinner size="sm" animation="border" className="me-2" />
                                            Adding...
                                        </>
                                    ) : (
                                        <>
                                            <i className="fas fa-plus me-2"></i>
                                            Add Ingredient
                                        </>
                                    )}
                                </Button>
                            </div>
                        </div>
                    </div>
                </Col>
            </Row>
            )}

            {/* Ingredients Table - show pending ingredients in create mode, or saved ingredients in update mode */}
            {mode === 'create' ? (
                pendingIngredients.length > 0 ? (
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>Image</th>
                                <th>GTIN</th>
                                <th>Type</th>
                                <th>Ingredient</th>
                                <th>Quantity</th>
                                {/* <th>Notes</th> */}
                                <th style={{ width: '100px' }}>Actions</th>
                            </tr>
                        </thead>
                        <tbody>
                            {pendingIngredients.map((ingredient) => (
                                <tr key={ingredient._tempId}>
                                    <td>
                                        {ingredient.stock_item_image ? (() => {
                                            const stockItemData = ingredient.stock_item_data
                                            const img = (
                                                <img
                                                    src={ingredient.stock_item_image}
                                                    alt="Ingredient Thumbnail"
                                                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                />
                                            )
                                            return stockItemData ? (
                                                <OverlayTrigger
                                                    placement="right"
                                                    overlay={
                                                        <Tooltip className="stock-item-tooltip">
                                                            <StockItemInfo stockItem={stockItemData} />
                                                        </Tooltip>
                                                    }
                                                >
                                                    {img}
                                                </OverlayTrigger>
                                            ) : img
                                        })() : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td>
                                        {ingredient.item_family ? '' : <CopyableText value={ingredient.gtin} toastLabel="GTIN" />}
                                    </td>
                                    <td>
                                        <Badge bg={ingredient.stock_item ? 'primary' : 'info'}>
                                            {ingredient.stock_item ? 'Stock Item' : 'Item Family'}
                                        </Badge>
                                    </td>
                                    <td>
                                        {ingredient.stock_item ? (
                                            <Link to={`/stock-item/${ingredient.stock_item}`} target="_blank">
                                                {ingredient.ingredient_name}
                                            </Link>
                                        ) : (
                                            <Link to={`/item-family/${ingredient.item_family}`} target="_blank">
                                                {ingredient.ingredient_name}
                                            </Link>
                                        )}
                                    </td>
                                    <td>
                                        {ingredient.input_mode === 'weight' && ingredient.weight_value ? (
                                            <span>{ingredient.weight_value} {ingredient.weight_unit}</span>
                                        ) : (
                                            <>
                                                <QuantityDisplay
                                                    casePack={ingredient.case_pack || 1}
                                                    uom={ingredient.unit_of_measure || ''}
                                                    value={ingredient.quantity}
                                                />
                                                {ingredient.item_family && !isSuperUnit(ingredient.unit_of_measure || '') && <span> items</span>}
                                            </>
                                        )}
                                    </td>
                                    {/* <td>{ingredient.notes || '-'}</td> */}
                                    <td>
                                        <Button
                                            variant="danger"
                                            size="sm"
                                            onClick={() => handleRemovePendingIngredient(ingredient._tempId)}
                                        >
                                            Remove
                                        </Button>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : null
            ) : (
                recipe?.ingredients && recipe.ingredients.length > 0 ? (
                    <Table striped bordered hover>
                        <thead>
                            <tr>
                                <th style={{ width: '60px' }}>Image</th>
                                <th>GTIN</th>
                                <th>Type</th>
                                <th>Ingredient</th>
                                <th style={{ minWidth: '280px' }}>Quantity</th>
                                <th>Waste %</th>
                                {/* <th>Notes</th> */}
                                {canModify && <th style={{ width: '150px' }}>Actions</th>}
                            </tr>
                        </thead>
                        <tbody>
                            {recipe.ingredients.map((ingredient) => (
                                <tr key={ingredient._id}>
                                    <td>
                                        {ingredient.stock_item_image ? (() => {
                                            const stockItemData = ingredient.stock_item_data
                                            const img = (
                                                <img
                                                    src={ingredient.stock_item_image}
                                                    alt="Ingredient Thumbnail"
                                                    style={{ width: '40px', height: '40px', objectFit: 'contain' }}
                                                />
                                            )
                                            return stockItemData ? (
                                                <OverlayTrigger
                                                    placement="right"
                                                    overlay={
                                                        <Tooltip className="stock-item-tooltip">
                                                            <StockItemInfo stockItem={stockItemData} />
                                                        </Tooltip>
                                                    }
                                                >
                                                    {img}
                                                </OverlayTrigger>
                                            ) : img
                                        })() : (
                                            <span className="text-muted">-</span>
                                        )}
                                    </td>
                                    <td>
                                        {ingredient.item_family ? '' : <CopyableText value={ingredient.gtin} toastLabel="GTIN" />}
                                    </td>
                                    <td>
                                        <Badge bg={ingredient.stock_item ? 'primary' : 'info'}>
                                            {ingredient.stock_item ? 'Stock Item' : 'Item Family'}
                                        </Badge>
                                    </td>
                                    <td>
                                        {ingredient.stock_item ? (
                                            <Link to={`/stock-item/${ingredient.stock_item}`} target="_blank">
                                                {ingredient.ingredient_name || ingredient.stock_item_name}
                                            </Link>
                                        ) : (
                                            <Link to={`/item-family/${ingredient.item_family}`} target="_blank">
                                                {ingredient.ingredient_name || ingredient.item_family_name}
                                            </Link>
                                        )}
                                    </td>
                                    <td>
                                        {editingIngredientId === ingredient._id ? (
                                            <QuantityPicker
                                                casePack={ingredient.case_pack || 1}
                                                uom={ingredient.unit_of_measure || ''}
                                                value={editQuantity}
                                                onChange={(qty) => setEditQuantity(qty)}
                                            />
                                        ) : (
                                            <>
                                                {ingredient.input_mode === 'weight' && ingredient.weight_value ? (
                                                    <span>{ingredient.weight_value} {ingredient.weight_unit}</span>
                                                ) : (
                                                    <>
                                                        <QuantityDisplay
                                                            casePack={ingredient.case_pack || 1}
                                                            uom={ingredient.unit_of_measure || ''}
                                                            value={ingredient.quantity}
                                                        />
                                                        {ingredient.item_family && !isSuperUnit(ingredient.unit_of_measure || '') && <span> items</span>}
                                                    </>
                                                )}
                                            </>
                                        )}
                                    </td>
                                    <td>
                                        <Badge bg={parseFloat(ingredient.waste_percentage) < 0 ? 'warning' : 'secondary'} className="bg-opacity-25 text-body rounded-pill">
                                            {ingredient.waste_percentage}%
                                        </Badge>
                                        {parseFloat(ingredient.waste_percentage) < 0 && (
                                            <OverlayTrigger
                                                placement="top"
                                                overlay={
                                                    <Tooltip>
                                                        Negative waste indicates actual consumption was less than recipe quantity — consider reducing the ingredient quantity
                                                    </Tooltip>
                                                }
                                            >
                                                <i className="fas fa-exclamation-triangle text-warning ms-1" />
                                            </OverlayTrigger>
                                        )}
                                    </td>
                                    {/* <td>{ingredient.notes || '-'}</td> */}
                                    {canModify && (
                                        <td>
                                            {editingIngredientId === ingredient._id ? (
                                                <>
                                                    <Button
                                                        variant="success"
                                                        size="sm"
                                                        className="me-1"
                                                        onClick={() => handleSaveEdit(ingredient._id)}
                                                        disabled={savingEdit}
                                                    >
                                                        {savingEdit ? <Spinner size="sm" /> : <i className="fas fa-check"></i>}
                                                    </Button>
                                                    <Button
                                                        variant="secondary"
                                                        size="sm"
                                                        onClick={handleCancelEdit}
                                                        disabled={savingEdit}
                                                    >
                                                        <i className="fas fa-times"></i>
                                                    </Button>
                                                </>
                                            ) : (
                                                <>
                                                    <Button
                                                        variant="primary"
                                                        size="sm"
                                                        className="me-1"
                                                        onClick={() => handleEditIngredient(ingredient)}
                                                        disabled={recipe.in_production?.is_active}
                                                    >
                                                        <i className="fas fa-pencil-alt"></i>
                                                    </Button>
                                                    <Button
                                                        variant="danger"
                                                        size="sm"
                                                        onClick={() => handleRemoveIngredient(ingredient._id)}
                                                        disabled={recipe.in_production?.is_active}
                                                    >
                                                        Remove
                                                    </Button>
                                                </>
                                            )}
                                        </td>
                                    )}
                                </tr>
                            ))}
                        </tbody>
                    </Table>
                ) : null
            )}

            {error && (
                <Alert variant="danger" onClose={() => setError(null)} dismissible className="mt-4">
                    {error}
                </Alert>
            )}

            <div className="mt-4">
                {mode === 'create' ? (
                    <Button
                        variant="success"
                        className="me-2"
                        onClick={submitHandler}
                    >
                        Create Recipe
                    </Button>
                ) : canModify && recipe?.is_active && (
                    <>
                        <Button
                            variant="success"
                            className="me-2"
                            onClick={submitHandler}
                        >
                            Update
                        </Button>
                        <Button
                            variant="danger"
                            className="me-2"
                            onClick={handleDeleteClick}
                        >
                            Delete
                        </Button>
                    </>
                )}
                {mode === 'update' && hasPermission('production.manage') && recipe?.is_active && (
                    <Button
                        variant="primary"
                        className="me-2"
                        onClick={() => navigate(`/recipe/${id}/execute`)}
                        disabled={!recipe?.ingredients?.length}
                    >
                        Execute
                    </Button>
                )}
                {mode === 'update' && recipe?.ingredients?.length > 0 && (
                    <Button
                        variant="secondary"
                        className="me-2"
                        onClick={handlePrintRecipe}
                    >
                        <i className="fas fa-print me-2"></i>
                        Print
                    </Button>
                )}
                <Button
                    variant="outline-primary"
                    onClick={() => navigate(backTo + returnFilters)}
                >
                    <i className="fas fa-arrow-left me-2"></i>
                    {backLabel}
                </Button>
            </div>

            <ConfirmationModal
                show={showDeleteModal}
                onHide={() => setShowDeleteModal(false)}
                onConfirm={confirmDelete}
                title="Confirm Deletion"
                message={`Are you sure you want to delete the recipe "${recipe?.name}"? All ingredients will also be deleted.`}
            />

            <StockItemModal
                show={showStockItemModal}
                handleClose={() => setShowStockItemModal(false)}
                handleStockItemCreated={handleStockItemCreated}
            />

        </Container>
    )
}

export default RecipeUpdateScreen
